/* eslint-disable react/forbid-dom-props, react/only-export-components -- this module
   serialises a standalone print document via renderToStaticMarkup; Fast Refresh does
   not apply, its class names bind to REPORT_DOCUMENT_STYLES, and platform primitives
   do not survive into the @page stylesheet */
import {Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, XAxis, YAxis} from 'recharts';
import {match} from 'ts-pattern';

import {renderToStaticMarkup} from 'react-dom/server';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import type {ChartReportDocument, ReportDocumentSection} from './ChartReportDocument';
import {createReportDocumentTables} from './createReportDocumentTables';
import {DOCUMENT_CHART_COLORS} from './DOCUMENT_CHART_COLORS';
import {DocumentLineChart} from './DocumentLineChart';
import {DocumentStackedBarShape} from './DocumentStackedBarShape';
import {
  formatReportDocumentSecondaryValue,
  formatReportDocumentValue,
  type ReportDocumentSecondaryValueOptions,
} from './formatReportDocumentValue';
import {getDocumentBarRadius} from './getDocumentBarRadius';
import {getReportDocumentGroups} from './getReportDocumentGroups';
import {createReportDocumentPageMargins, REPORT_DOCUMENT_STYLES} from './REPORT_DOCUMENT_STYLES';
import {splitReportDocumentTableColumns} from './splitReportDocumentTableColumns';

const CHART_WIDTH = 690;
const FULL_CHART_HEIGHT = 180;
const COMPACT_CHART_WIDTH = 310;
const NARROW_CHART_WIDTH = 200;
const COMPACT_CHART_HEIGHT = 100;
const MAX_RANKED_CHART_ROWS = 12;
const HORIZONTAL_AXIS_LABEL_MAX_LENGTH = 30;
const MAX_KEEP_TOGETHER_ROWS = 25;
const LINE_DOT_RADIUS = 2;
const AXIS_TICK = {fontSize: 11, fill: DOCUMENT_CHART_COLORS.axisLabel};

function formatHorizontalAxisLabel(value: string) {
  return value.length > HORIZONTAL_AXIS_LABEL_MAX_LENGTH
    ? `${value.slice(0, HORIZONTAL_AXIS_LABEL_MAX_LENGTH - 1)}…`
    : value;
}

function createChartData(section: ReportDocumentSection) {
  return section.categories.map((category, categoryIndex) => {
    const point: Record<string, number | string | null> & {category: string} = {category};
    const isUnknownStack =
      section.isStacked && section.series.some((series) => isNil(series.values[categoryIndex]));
    section.series.forEach((series, seriesIndex) => {
      point[`series${seriesIndex}`] = isUnknownStack
        ? null
        : (series.values[categoryIndex] ?? null);
    });
    return point;
  });
}

function DocumentLegend(props: {section: ReportDocumentSection; hidePrimary?: boolean}) {
  return (
    <div className="document-legend">
      {props.section.series
        .filter(
          (series) =>
            !props.hidePrimary || series.strokeDasharray || series.label !== props.section.title
        )
        .map((series) => (
          <span key={series.id}>
            <svg width={20} height={8} aria-hidden="true">
              {props.section.chartType === 'line' || series.renderAs === 'line' ? (
                <line
                  x1={0}
                  x2={20}
                  y1={4}
                  y2={4}
                  stroke={series.color}
                  strokeWidth={2}
                  strokeDasharray={series.strokeDasharray}
                />
              ) : (
                <rect x={6} y={0} width={8} height={8} rx={2} fill={series.color} />
              )}
            </svg>
            {series.strokeDasharray ? '† ' : ''}
            {series.label}
          </span>
        ))}
    </div>
  );
}

function DocumentChart(props: {
  document: ChartReportDocument;
  section: ReportDocumentSection;
  compact: boolean;
  narrow: boolean;
}) {
  const isHorizontal = props.section.orientation === 'horizontal';
  const data = createChartData(props.section);
  const compactWidth = props.narrow ? NARROW_CHART_WIDTH : COMPACT_CHART_WIDTH;
  const width = props.compact ? compactWidth : CHART_WIDTH;
  const compactHeight = props.narrow ? 110 : COMPACT_CHART_HEIGHT;
  const height = props.compact ? compactHeight : FULL_CHART_HEIGHT;
  const formatAxisValue = (value: number) =>
    new Intl.NumberFormat(props.document.locale, {
      maximumFractionDigits: 1,
      notation: 'compact',
    }).format(value);
  if (data.length === 0 || props.section.series.length === 0) {
    return <p className="empty-chart">{i18n.t('general.labels.noData')}</p>;
  }
  if (
    isHorizontal &&
    props.section.series.length === 1 &&
    (props.section.tableColumns ?? props.section.series).length === 1 &&
    props.section.categoryType !== 'month'
  ) {
    return null;
  }
  if (props.section.chartType === 'line') {
    return (
      <>
        <div className="chart">
          <DocumentLineChart
            data={data}
            formatAxisValue={formatAxisValue}
            height={height}
            section={props.section}
            width={width}
          />
        </div>
        <DocumentLegend section={props.section} hidePrimary />
      </>
    );
  }
  // Keep every horizontal chart bounded; the complete exact data table follows.
  const chunks = isHorizontal
    ? Array.from({length: Math.ceil(data.length / MAX_RANKED_CHART_ROWS)}, (_, index) =>
        data.slice(index * MAX_RANKED_CHART_ROWS, (index + 1) * MAX_RANKED_CHART_ROWS)
      )
    : [data];
  const domain = data.reduce(
    (bounds, point) =>
      props.section.series.reduce((extent, _, index) => {
        const value = point[`series${index}`];
        return typeof value === 'number'
          ? {min: Math.min(extent.min, value), max: Math.max(extent.max, value)}
          : extent;
      }, bounds),
    {min: 0, max: 0}
  );
  return (
    <>
      {chunks.map((chunk) => (
        <div className="chart" key={chunk[0].category}>
          <ComposedChart
            width={width}
            height={isHorizontal ? Math.max(height, chunk.length * 27 + 60) : height}
            data={chunk}
            layout={isHorizontal ? 'vertical' : 'horizontal'}
            margin={{top: 12, left: 0, right: 12, bottom: 4}}
          >
            <CartesianGrid
              vertical={isHorizontal}
              horizontal={!isHorizontal}
              stroke={DOCUMENT_CHART_COLORS.grid}
            />
            {match(isHorizontal)
              .with(true, () => (
                <>
                  <XAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatAxisValue}
                    domain={[domain.min, domain.max]}
                    tick={AXIS_TICK}
                  />
                  <YAxis
                    type="category"
                    dataKey="category"
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatHorizontalAxisLabel}
                    tick={AXIS_TICK}
                    width={220}
                  />
                </>
              ))
              .otherwise(() => (
                <>
                  <XAxis
                    type="category"
                    dataKey="category"
                    interval={Math.max(0, Math.ceil(data.length / (props.compact ? 4 : 12)) - 1)}
                    axisLine={false}
                    tickLine={false}
                    tick={AXIS_TICK}
                  />
                  <YAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatAxisValue}
                    tick={AXIS_TICK}
                    width={65}
                  />
                </>
              ))}
            {match(Boolean(props.section.showZeroLine))
              .with(true, () =>
                isHorizontal ? (
                  <ReferenceLine x={0} stroke={DOCUMENT_CHART_COLORS.referenceLine} />
                ) : (
                  <ReferenceLine y={0} stroke={DOCUMENT_CHART_COLORS.referenceLine} />
                )
              )
              .otherwise(() => null)}
            {props.section.series.map((series, index) =>
              series.renderAs === 'line' ? (
                <Line
                  key={series.id}
                  dataKey={`series${index}`}
                  name={series.label}
                  type="linear"
                  stroke={series.color}
                  strokeWidth={2}
                  dot={{r: LINE_DOT_RADIUS}}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ) : (
                <Bar
                  key={series.id}
                  dataKey={`series${index}`}
                  name={series.label}
                  fill={series.color}
                  stackId={props.section.isStacked ? 'stack' : undefined}
                  radius={getDocumentBarRadius(isHorizontal)}
                  maxBarSize={26}
                  isAnimationActive={false}
                  shape={
                    props.section.isStacked ? (
                      <DocumentStackedBarShape
                        isHorizontal={isHorizontal}
                        seriesCount={props.section.series.length}
                        seriesIndex={index}
                      />
                    ) : undefined
                  }
                />
              )
            )}
          </ComposedChart>
        </div>
      ))}
      <DocumentLegend section={props.section} />
    </>
  );
}

type DocumentTable = ReturnType<typeof createReportDocumentTables>[number];

function DocumentChartDataTable(props: {document: ChartReportDocument; table: DocumentTable}) {
  const table = props.table;
  const isRanking = !table.isMonthly && table.columns.length === 1;
  const extent = table.rows.reduce(
    (bounds, row) => {
      const value = row.values[0] ?? 0;
      return {min: Math.min(bounds.min, value), max: Math.max(bounds.max, value)};
    },
    {min: 0, max: 0}
  );
  const range = extent.max - extent.min || 1;
  const zero = (-extent.min / range) * 100;
  const rowGroups =
    isRanking && table.rows.length > 20 && table.rows.length <= 40
      ? [
          table.rows.slice(0, Math.ceil(table.rows.length / 2)),
          table.rows.slice(Math.ceil(table.rows.length / 2)),
        ]
      : [table.rows];
  const columnGroups = splitReportDocumentTableColumns(table, props.document);
  return (
    <div className={rowGroups.length > 1 ? 'ranked-tables' : undefined}>
      {rowGroups.flatMap((rows, rowGroupIndex) =>
        columnGroups.map((indices) => (
          <div
            className={
              rows.length <= MAX_KEEP_TOGETHER_ROWS ? 'table-block keep-together' : 'table-block'
            }
            key={`${rowGroupIndex}-${indices.join('-')}`}
          >
            <table className="chart-data-table">
              <caption>
                {table.title}
                {rowGroups.length > 1 ? ` · ${rowGroupIndex + 1}/${rowGroups.length}` : ''}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{i18n.t('general.labels.category')}</th>
                  {indices.map((index) => {
                    const column = table.columns[index];
                    const unit =
                      column.kind === 'currency'
                        ? props.document.currency
                        : (column.unit ?? (column.kind === 'percentage' ? '%' : undefined));
                    return (
                      <th scope="col" key={column.key}>
                        {column.label}
                        {unit && !column.label.endsWith(`(${unit})`) ? ` (${unit})` : ''}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={`${row.category}-${row.index}`}
                    className={row.incomplete ? 'incomplete-row' : undefined}
                  >
                    <th scope="row">
                      {row.category}
                      {row.incomplete ? ' †' : ''}
                      {match(isRanking && !isNil(row.values[0]))
                        .with(true, () => {
                          const end = (((row.values[0] ?? 0) - extent.min) / range) * 100;
                          return (
                            <svg
                              className="category-bar"
                              aria-hidden="true"
                              viewBox="0 0 100 8"
                              preserveAspectRatio="none"
                            >
                              <line
                                x1={zero}
                                x2={zero}
                                y1={0}
                                y2={8}
                                stroke="#8793a7"
                                strokeWidth={0.3}
                              />
                              <rect
                                x={Math.min(zero, end)}
                                y={2}
                                width={Math.abs(end - zero)}
                                height={4}
                                fill={table.columns[0].color ?? '#165ddf'}
                              />
                            </svg>
                          );
                        })
                        .otherwise(() => null)}
                    </th>
                    {indices.map((index) => (
                      <td key={table.columns[index].key}>
                        {formatReportDocumentValue(
                          row.values[index],
                          table.columns[index],
                          props.document,
                          {
                            table: true,
                            percentage: table.columns[index].percentages
                              ? (table.columns[index].percentages[row.index] ?? null)
                              : undefined,
                          }
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {match(
                props.document.includeContext &&
                  rowGroupIndex === rowGroups.length - 1 &&
                  indices.some((index) => !isNil(table.columns[index].summary))
              )
                .with(true, () => (
                  <tfoot>
                    <tr>
                      <th scope="row">{i18n.t('general.labels.summary')}</th>
                      {indices.map((index) => {
                        const summary = table.columns[index].summary;
                        return (
                          <td key={table.columns[index].key}>
                            {isNil(summary)
                              ? '—'
                              : formatReportDocumentValue(summary.value, summary, props.document, {
                                  table: true,
                                  percentage: summary.percentage,
                                })}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                ))
                .otherwise(() => null)}
            </table>
          </div>
        ))
      )}
    </div>
  );
}

function DocumentSummarySecondaryValue(props: ReportDocumentSecondaryValueOptions) {
  const value = formatReportDocumentSecondaryValue(props);
  if (isNil(value)) {
    return null;
  }
  return <span className="summary-exact">{value}</span>;
}

function ReportSectionContent(props: {
  document: ChartReportDocument;
  section: ReportDocumentSection;
  index: number;
  compact: boolean;
  narrow: boolean;
}) {
  const summaries = props.section.summaryItems ?? [];
  const primary = summaries.findLast((item) => item.kind === 'currency') ?? summaries[0];
  const secondary = summaries.filter((item) => item.kind === 'percentage' && item !== primary);
  return (
    <section className="report-section">
      <h2>
        <span>{props.index + 1}</span>
        {props.section.title}
      </h2>
      {match(props.document.includeContext && summaries.length > 0)
        .with(true, () =>
          props.compact ? (
            <div className="compact-summary">
              <strong>
                {primary
                  ? formatReportDocumentValue(primary.value, primary, props.document, {
                      compact: true,
                      percentage: primary.percentage,
                    })
                  : '—'}
              </strong>
              <DocumentSummarySecondaryValue document={props.document} item={primary} />
              {secondary.map((item) => (
                <small key={item.label}>
                  {item.label}:{' '}
                  {formatReportDocumentValue(item.value, item, props.document, {
                    percentage: item.percentage,
                  })}
                </small>
              ))}
            </div>
          ) : (
            <div
              className={summaries.length === 5 ? 'summary-grid five-summaries' : 'summary-grid'}
            >
              {summaries.map((item) => (
                <div className="summary-card" key={item.label} style={{borderColor: item.color}}>
                  <span className="summary-dot" style={{background: item.color}} />
                  <span className="summary-label">{item.label}</span>
                  <strong className="summary-value">
                    {formatReportDocumentValue(item.value, item, props.document, {
                      compact: true,
                      percentage: item.percentage,
                    })}
                  </strong>
                  <span className="summary-exact">
                    {formatReportDocumentValue(item.value, item, props.document, {
                      percentage: item.percentage,
                    })}
                  </span>
                  <DocumentSummarySecondaryValue document={props.document} item={item} />
                </div>
              ))}
            </div>
          )
        )
        .otherwise(() => null)}
      {match(Boolean(props.section.description))
        .with(true, () => <p className="section-description">{props.section.description}</p>)
        .otherwise(() => null)}
      <DocumentChart
        document={props.document}
        section={props.section}
        compact={props.compact}
        narrow={props.narrow}
      />
    </section>
  );
}

function ReportDocument(props: {document: ChartReportDocument}) {
  const metadata = [
    `${i18n.t('general.labels.generated')} ${props.document.generatedAt}`,
    props.document.generatedBy,
    ...props.document.metadata,
  ].filter(Boolean);
  const groups = getReportDocumentGroups(props.document);
  const printedSections = groups.flatMap((group) => group.sections);
  return (
    <html lang={props.document.locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{props.document.title}</title>
        <style>{REPORT_DOCUMENT_STYLES}</style>
        <style>
          {createReportDocumentPageMargins(
            `${props.document.context} · ${props.document.title}`,
            `${props.document.footer}\n${metadata.join(' · ')}`
          )}
        </style>
      </head>
      <body>
        <header className="page-header">
          <span className="brand">OMNETIC</span>
          <span className="context">
            {props.document.context} · {props.document.title}
          </span>
        </header>
        <main>
          {groups.map((group, groupIndex) => {
            const compact = group.sections.length > 1;
            const tables = createReportDocumentTables(group);
            const legacyNotes = group.sections.flatMap((section) =>
              section.note ? [section.note] : []
            );
            const redundantLegacyNotes = new Set(
              legacyNotes.filter((note) =>
                legacyNotes.some((otherNote) => otherNote.endsWith(` ${note}`))
              )
            );
            const printedNotes = new Set<string>();
            const uniqueNotes = group.sections
              .map((section) =>
                [
                  ...(section.note && !redundantLegacyNotes.has(section.note)
                    ? [section.note]
                    : []),
                  ...(section.notes ?? []),
                ]
                  .filter((note) => {
                    if (printedNotes.has(note)) {
                      return false;
                    }
                    printedNotes.add(note);
                    return true;
                  })
                  .join(' ')
              )
              .filter(Boolean);
            const columnsClass = group.sections.length > 2 ? 'columns-3' : 'columns-2';
            return (
              <article
                className={compact ? 'report-group grouped' : 'report-group'}
                key={group.sections.map((section) => section.id).join('-')}
              >
                {match(groupIndex === 0)
                  .with(true, () => (
                    <>
                      <h1>{props.document.title}</h1>
                      <p className="metadata">{metadata.join(' · ')}</p>
                    </>
                  ))
                  .otherwise(() => null)}
                {match(props.document.includeContext && props.document.filters.length > 0)
                  .with(true, () => (
                    <div className="filters">
                      {props.document.filters.map((filter) => (
                        <div className="filter-card" key={filter.label}>
                          <span>{filter.label}</span>
                          <strong>{filter.value}</strong>
                        </div>
                      ))}
                    </div>
                  ))
                  .otherwise(() => null)}
                <div className={`group-charts ${columnsClass}`}>
                  {group.sections.map((section) => (
                    <ReportSectionContent
                      key={section.id}
                      document={props.document}
                      section={section}
                      compact={compact}
                      narrow={group.sections.length > 2}
                      index={printedSections.findIndex((candidate) => candidate.id === section.id)}
                    />
                  ))}
                </div>
                {tables.map((table) => (
                  <DocumentChartDataTable key={table.id} document={props.document} table={table} />
                ))}
                <div className="section-notes">
                  {uniqueNotes.map((note) => (
                    <p className="section-note" key={note}>
                      {note}
                    </p>
                  ))}
                </div>
              </article>
            );
          })}
        </main>
        <footer className="document-footer">
          <span>{props.document.footer}</span>
          <span>{metadata.join(' · ')}</span>
        </footer>
      </body>
    </html>
  );
}

export function createChartReportDocument(document: ChartReportDocument) {
  return `<!doctype html>${renderToStaticMarkup(<ReportDocument document={document} />)}`;
}
